import { Component, Output, SimpleChanges, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { LoggerService } from '../../services/logger.service';
import { GlobalModalService } from '../../shared/global-modal/global-modal.service';
import { UserService } from '../../services/user.service';
import { SocietyService } from '../../services/society.service';
import { DepartmentService } from '../../services/department.service';
import { TypeService } from '../../services/type.service';
import { SystemService } from '../../services/systems.service';
import { ProyectoService } from '../../services/proyecto.service';
import { Project } from '../../models/project.model';
import { Society } from '../../models/society.model';
import { Department } from '../../models/department.model';
import { Type } from '../../models/type.model';
import { System } from '../../models/system.model';
import { User } from '../../models/user.model';
import { Router } from '@angular/router';

@Component({
    selector: 'app-proyecto-form',
    templateUrl: './proyecto-form.component.html',
    styleUrls: ['./proyecto-form.component.scss']
})
export class ProyectoFormComponent implements OnInit, OnDestroy, OnChanges {
  selectedProject: Project = new Project();
  @Output() projectChange = new EventEmitter<Project>();
  isEditing: boolean = false;
  projectSaved = false;
  showModal = false;
  selectedSociety: Society = new Society();
  societies: Society[] = [];
  departments: Department[] = [];
  allDepartments: Department[] = [];
  types: Type[] = [];
  user: User | any;
  selectedTab: string = 'estimacion';

  allSystems: System[] = [];
  filteredSystems: System[] = [];

  totalHorasEstimadas: number = 0;
  totalHorasEjecutadas: number = 0;

  groupedExecutions: { description: string, tasks: { task: any, executions: any[] }[] }[] = [];
  unplannedExecutions: any[] = [];
  transversalExecutions: any[] = [];

  isLoadingSocieties: boolean = false;
  isLoadingDepartments: boolean = false;
  isLoadingTypes: boolean = false;
  isLoadingSystems: boolean = false;

  constructor(
    private societyService: SocietyService,
    private departmentService: DepartmentService,
    private typeService: TypeService,
    private systemService: SystemService,
    private globalModalService: GlobalModalService,
    private userService: UserService,
    private proyectService: ProyectoService,
    private logger: LoggerService,
    private router: Router
  ) {}

  /**
   * MODIFICADO 1a-fix: orquestación de la carga inicial corregida.
   *
   * El bug anterior: al cargar un proyecto existente, los catálogos (sociedades,
   * sistemas) y los datos del proyecto se cargaban en paralelo sin coordinarse,
   * lo que producía una race condition donde el multi-select de sistemas se
   * pintaba con un catálogo vacío y los sistemas preseleccionados desaparecían.
   *
   * Solución: tras recibir el proyecto, sincronizamos explícitamente
   * `selectedSociety` con la sociedad del proyecto y refrescamos el filtro de
   * sistemas. Llamamos a `selectSociety()` y `selectType()` también de forma
   * explícita por idempotencia (no pasa nada si ya se han llamado desde los
   * loadXxx, simplemente vuelven a resolver con datos consistentes).
   */
  ngOnInit(): void {
    this.getUser();
    this.loadDepartments();
    this.loadSocieties();
    this.loadTypes();
    this.loadSystems();

    const savedProject = sessionStorage.getItem('selectedProject');
    if (savedProject) {
      this.selectedProject = JSON.parse(savedProject);
      this.proyectService.getProyectoById(this.selectedProject.id.toString()).subscribe(
        (proyecto) => {
          this.selectedProject = proyecto;
          if (!Array.isArray(this.selectedProject.systems)) {
            this.selectedProject.systems = [];
          }
          // Reconciliar selecciones con los catálogos que pueden o no haber
          // llegado ya. Cada método es defensivo: si su catálogo aún no
          // está disponible, no hace nada; los loadXxx volverán a llamarlos
          // cuando lleguen los datos.
          this.selectSociety();
          this.selectType();
          this.refreshFilteredSystems();
          this.calcularTotales();
        },
        (error) => { this.logger.error('Error al cargar el proyecto', error); }
      );
      this.isEditing = true;
      this.projectSaved = true;
    } else {
      this.newProject();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.selectedProject.id === undefined) {
      this.setFechaActual();
      this.selectedSociety = new Society();
      this.onSocietyChange();
    }
  }

  ngOnDestroy(): void {}

  calcularTotales(): void {
    this.totalHorasEstimadas = 0;
    this.totalHorasEjecutadas = 0;

    if (this.selectedProject.estimation?.requirements) {
      this.totalHorasEstimadas = this.selectedProject.estimation.requirements.reduce((total, req) => {
        const horasReq = req.tasks?.reduce((sub, task) => sub + Number(task.estimatetime || 0), 0) || 0;
        return total + horasReq;
      }, 0);
    }

    if (this.selectedProject.taskexecutions) {
      this.totalHorasEjecutadas = this.selectedProject.taskexecutions.reduce((total, exec) => {
        return total + Number(exec.executedtime || 0);
      }, 0);
    }

    this.agruparEjecuciones();
  }

  agruparEjecuciones(): void {
    this.groupedExecutions = [];
    this.unplannedExecutions = [];
    this.transversalExecutions = [];

    if (!this.selectedProject.taskexecutions || this.selectedProject.taskexecutions.length === 0) return;

    const allExecutions = [...this.selectedProject.taskexecutions];
    const matchedExecutions = new Set<any>();

    if (this.selectedProject.estimation && this.selectedProject.estimation.requirements) {
      this.selectedProject.estimation.requirements.forEach((req: any) => {
        const reqGroup = { description: req.description, tasks: [] as any[] };

        if (req.tasks) {
          req.tasks.forEach((task: any) => {
            const taskExecs = allExecutions.filter(ex => ex.taskid === task.id);
            taskExecs.forEach(ex => matchedExecutions.add(ex));

            reqGroup.tasks.push({ task: task, executions: taskExecs });
          });
        }

        if (reqGroup.tasks.length > 0) {
          this.groupedExecutions.push(reqGroup);
        }
      });
    }

    const orphans = allExecutions.filter(ex => !matchedExecutions.has(ex));
    this.transversalExecutions = orphans.filter(ex => ex.istransversal === true);
    this.unplannedExecutions = orphans.filter(ex => !ex.istransversal);
  }

  newProject(): void {
    if (this.isEditing) {
      this.globalModalService.confirm('Nuevo Proyecto', 'Hay cambios sin guardar. ¿Crear proyecto?').subscribe((confirmed) => {
        if(confirmed) this.initializeNewProject();
      });
    } else {
      this.initializeNewProject();
    }
    this.projectSaved = false;
  }

  initializeNewProject(): void {
    this.selectedProject = new Project();
    this.isEditing = true;
    this.totalHorasEstimadas = 0;
    this.totalHorasEjecutadas = 0;
    this.groupedExecutions = [];
    this.unplannedExecutions = [];
    this.transversalExecutions = [];
    this.filteredSystems = [];
  }

  loadSocieties(): void {
    this.isLoadingSocieties = true;
    this.societyService
      .getAllSocieties()
      .pipe(finalize(() => { this.isLoadingSocieties = false; }))
      .subscribe(
        data => { this.societies = data; this.selectSociety(); },
        error => { this.logger.error('Error cargando sociedades', error); }
      );
  }

  /**
   * MODIFICADO 1a-fix: defensivo + refresca sistemas tras resolver la sociedad.
   *
   * Sin el refresco final, si `loadSocieties` termina DESPUÉS de cargar el
   * proyecto, `selectedSociety` se actualizaba pero `filteredSystems` se
   * quedaba vacío hasta el siguiente cambio manual.
   */
  selectSociety(): void {
    if (!this.societies || this.societies.length === 0) return;
    const targetId = this.selectedProject?.department?.society?.id;
    const foundSociety = targetId ? this.societies.find(d => d.id === targetId) : null;
    if (foundSociety) {
      this.selectedSociety = foundSociety;
      if (this.selectedProject.department) {
        this.selectedProject.department.society = foundSociety;
      }
    } else {
      this.selectedSociety = new Society();
    }
    this.refreshFilteredSystems();
  }

  onSocietyChange(): void {
    const previousClientId = this.selectedProject?.department?.society?.client?.id;
    const newClientId = this.selectedSociety?.client?.id;

    this.filterDepartmentsBySociety();
    this.selectedProject.department = new Department();

    if (!newClientId || newClientId !== previousClientId) {
      this.selectedProject.systems = [];
    }
    this.refreshFilteredSystems();

    this.onProjectChange();
  }

  compareSocieties(s1: Society, s2: Society): boolean {
    return s1 && s2 ? s1.id === s2.id : s1 === s2;
  }

  loadDepartments(): void {
    this.isLoadingDepartments = true;
    this.departmentService
      .getAllDepartments()
      .pipe(finalize(() => { this.isLoadingDepartments = false; }))
      .subscribe(
        data => { this.allDepartments = data; this.filterDepartmentsBySociety(); },
        error => { this.logger.error('Error cargando departamentos', error); }
      );
  }

  filterDepartmentsBySociety(): void {
    if (this.selectedSociety && this.selectedSociety.id) {
      this.departments = this.allDepartments.filter(dep => dep.society?.id === this.selectedSociety.id);
    } else {
      this.departments = [...this.allDepartments];
    }
  }

  onDepartmentChange(): void {
    const selectedDepartment = this.selectedProject.department;
    if (selectedDepartment && selectedDepartment.society) {
      this.selectedProject.department.society = selectedDepartment.society;
      this.selectedSociety = selectedDepartment.society;
    }
    this.onProjectChange();
  }

  compareDepartments(d1: Department, d2: Department): boolean {
    return d1 && d2 ? d1.id === d2.id : d1 === d2;
  }

  loadTypes(): void {
    this.isLoadingTypes = true;
    this.typeService
      .getAllTypes()
      .pipe(finalize(() => { this.isLoadingTypes = false; }))
      .subscribe(
        data => { this.types = data; this.selectType(); },
        error => { this.logger.error('Error cargando tipos', error); }
      );
  }

  selectType(): void {
    if (!this.types || this.types.length === 0) return;
    const foundType = this.types.find(d => d.id === this.selectedProject.type?.id);
    if (foundType) this.selectedProject.type = foundType;
  }

  compareTypes(d1: Type, d2: Type): boolean {
    return d1 && d2 ? d1.id === d2.id : d1 === d2;
  }

  loadSystems(): void {
    this.isLoadingSystems = true;
    this.systemService
      .getAllSystems()
      .pipe(finalize(() => { this.isLoadingSystems = false; }))
      .subscribe(
        data => { this.allSystems = data || []; this.refreshFilteredSystems(); },
        error => { this.logger.error('Error cargando sistemas', error); }
      );
  }

  /**
   * MODIFICADO 1a-fix: además de filtrar el catálogo, **rehidrata las
   * referencias de selectedProject.systems** para que apunten a los objetos
   * del catálogo (`allSystems`). Esto garantiza que el multi-select detecte
   * los preseleccionados aunque el backend haya devuelto objetos `systems`
   * con un grafo distinto (ej. sin `client` anidado, o con propiedades extra
   * de la tabla pivote).
   *
   * Sin esto: el multi-select compara por `id` y funciona en isSelected(),
   * pero las "badges" que pinta usan la referencia de `selected`, que puede
   * no coincidir con la del item del catálogo y generar inconsistencias
   * sutiles (ej. al deseleccionar y volver a marcar).
   */
  refreshFilteredSystems(): void {
    const clientId = this.selectedSociety?.client?.id;
    if (!clientId) {
      this.filteredSystems = [];
      return;
    }
    this.filteredSystems = this.allSystems.filter(s => s?.client?.id === clientId);

    if (Array.isArray(this.selectedProject?.systems) && this.selectedProject.systems.length > 0 && this.filteredSystems.length > 0) {
      const byId = new Map(this.filteredSystems.map(s => [s.id, s]));
      this.selectedProject.systems = this.selectedProject.systems
        .map(s => byId.get(s.id) || s)
        .filter(s => !!s);
    }
  }

  onSystemsChange(systems: System[]): void {
    this.selectedProject.systems = systems || [];
    this.onProjectChange();
  }

  onProjectChange(): void {
    this.projectChange.emit(this.selectedProject);
  }

  setFechaActual() {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    this.selectedProject.creationdate = `${year}-${month}-${day}`;
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
  }

  saveEntity() {
    this.proyectService.saveProject(this.selectedProject).subscribe({
      next: (response) => {
        this.globalModalService.showSuccess('Guardar Proyecto', 'Proyecto actualizado exitosamente');
        this.projectSaved = true;
        this.calcularTotales();
        this.onProjectChange();
      },
      error: (error) => {
        this.logger.error('Error actualizando proyecto', error);
      }
    });
  }

  deleteEntity() {
    if (!this.selectedProject || !this.selectedProject.id) return;

    this.globalModalService.confirm(
      'Eliminar Proyecto',
      `¿Estás seguro de que deseas eliminar el proyecto "${this.selectedProject.name}"? Esta acción no se puede deshacer.`
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.proyectService.deleteProject(this.selectedProject.id.toString()).subscribe({
          next: () => {
            this.globalModalService.showSuccess('Proyecto Eliminado', 'El proyecto ha sido eliminado correctamente.');
            sessionStorage.removeItem('selectedProject');
            sessionStorage.removeItem('selectedEstimation');
            this.router.navigate(['/app/home']);
          },
          error: (error) => {
            this.logger.error('Error al eliminar proyecto', error);
          }
        });
      }
    });
  }

  cancel() {
    this.router.navigate(['/app/proyectos']);
  }

  abrirEstimacion() {
    if (this.selectedProject.estimation) {
      sessionStorage.setItem('selectedEstimation', JSON.stringify(this.selectedProject.estimation));
      this.router.navigate(['/app/estimaciones']);
    }
  }

  abrirGestorEjecuciones() {
    if (this.selectedProject) {
      sessionStorage.setItem('selectedProject', JSON.stringify(this.selectedProject));
      this.router.navigate(['/app/ejecuciones']);
    }
  }

  getUser() {
    this.userService.user$.subscribe(user => this.user = user);
    if (!this.user) {
      const storedUser = sessionStorage.getItem('currentUser');
      if (storedUser) this.userService.updateUser(JSON.parse(storedUser));
    }
  }
}